import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  CheckCircle2,
  Download,
  Inbox,
  Settings2,
  XCircle,
} from "lucide-react";
import {
  actions,
  listLocations,
  loadRequestInbox,
  requestInboxAttachmentUrl,
} from "@/lib/api";
import type { Location, RequestInboxMessage } from "@/lib/types";
import { displayDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AssistedExtractionCard,
  type ExtractionSuggestion,
} from "@/assisted-extraction";

type Props = {
  organisationId: string;
  refresh: number;
  query: string;
  canRequisition: boolean;
  canProcure: boolean;
  canManage: boolean;
  changed: () => void;
};

export function RequestInbox(props: Props) {
  const [data, setData] =
    useState<Awaited<ReturnType<typeof loadRequestInbox>>>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<RequestInboxMessage>();
  const [configuring, setConfiguring] = useState(false);
  useEffect(() => {
    let live = true;
    setError("");
    Promise.all([
      loadRequestInbox(props.organisationId),
      listLocations(props.organisationId),
    ])
      .then(([inbox, rows]) => {
        if (live) {
          setData(inbox);
          setLocations(rows);
        }
      })
      .catch((reason: Error) => {
        if (live) setError(reason.message);
      });
    return () => {
      live = false;
    };
  }, [props.organisationId, props.refresh]);
  const rows = useMemo(() => {
    const needle = props.query.trim().toLowerCase();
    if (!needle) return data?.messages || [];
    return (data?.messages || []).filter((row) =>
      [
        row.subject,
        row.sender_email,
        row.sender_name,
        row.status,
        row.raw_sha256,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [data?.messages, props.query]);
  if (error)
    return (
      <div className="empty-state">
        <strong>Inbox unavailable</strong>
        <p>{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="empty-state">
        <p>Loading company request inbox…</p>
      </div>
    );
  return (
    <>
      <header className="page-heading mb-6">
        <div>
          <p className="eyebrow">REQUEST INTAKE</p>
          <h1>Company request inbox</h1>
          <p>
            Email evidence is preserved before a verified requester converts it
            into the canonical requisition workflow.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.address ? (
            <span className="rounded-md border px-3 py-2 font-mono text-sm">
              {data.address.address}
            </span>
          ) : (
            <Badge value="not configured" />
          )}
          {props.canManage && (
            <Button variant="outline" onClick={() => setConfiguring(true)}>
              <Settings2 className="size-4" />
              Configure
            </Button>
          )}
        </div>
      </header>
      <div className="mb-4 rounded-lg border border-[var(--border)] bg-[var(--surface-muted)] p-4 text-sm">
        <strong>Evidence boundary.</strong> Unknown senders are quarantined.
        HTML is never rendered here. Source and attachment hashes are immutable,
        and conversion requires an explicit review.
      </div>
      {rows.length ? (
        <div className="table-shell overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Received</th>
                <th>Sender</th>
                <th>Request</th>
                <th>Evidence</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{displayDate(row.received_at)}</td>
                  <td>
                    <strong>{row.sender_name || row.sender_email}</strong>
                    <small className="block text-[var(--text-secondary)]">
                      {row.sender_email}
                    </small>
                  </td>
                  <td>
                    {row.subject}
                    <small className="block text-[var(--text-secondary)]">
                      {(row.text_body || "No plain-text body").slice(0, 100)}
                    </small>
                  </td>
                  <td>
                    <span className="font-mono text-xs">
                      {row.raw_sha256.slice(0, 12)}…
                    </span>
                    <small className="block text-[var(--text-secondary)]">
                      {row.request_inbox_attachments?.length || 0} attachment(s)
                    </small>
                  </td>
                  <td>
                    <Badge value={row.status} />
                    {row.quarantine_reason && (
                      <small className="block max-w-56 text-[var(--danger)]">
                        {row.quarantine_reason}
                      </small>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(row)}
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Inbox className="mx-auto mb-3 size-8" />
          <strong>No visible inbox requests</strong>
          <p>
            Send a request from an active member email to the configured
            address.
          </p>
        </div>
      )}
      <InboxConfiguration
        open={configuring}
        onOpenChange={setConfiguring}
        organisationId={props.organisationId}
        currentAddress={data.address?.address || ""}
        onDone={props.changed}
      />
      <InboxReview
        open={Boolean(selected)}
        onOpenChange={(open) => !open && setSelected(undefined)}
        organisationId={props.organisationId}
        message={selected}
        locations={locations}
        canAct={props.canRequisition || props.canProcure || props.canManage}
        onDone={() => {
          setSelected(undefined);
          props.changed();
        }}
      />
    </>
  );
}

function InboxConfiguration({
  open,
  onOpenChange,
  organisationId,
  currentAddress,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  currentAddress: string;
  onDone: () => void;
}) {
  const [address, setAddress] = useState(currentAddress);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setAddress(currentAddress), [currentAddress, open]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await actions.configureRequestInbox(
        organisationId,
        address,
        "resend",
      );
      onDone();
      onOpenChange(false);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="INBOUND ROUTE"
          title="Configure company request inbox"
          description="Use the inbound address provisioned at your email provider. Webhook deliveries must use the protected RAQOZ HMAC contract."
        />
        <form className="space-y-4 p-6" onSubmit={submit}>
          <div>
            <Label htmlFor="inbox-address">Inbound email address</Label>
            <Input
              id="inbox-address"
              type="email"
              required
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="requests@inbound.example.com"
            />
          </div>
          {error && <p className="text-sm text-[var(--danger)]">{error}</p>}
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save governed address"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InboxReview({
  open,
  onOpenChange,
  organisationId,
  message,
  locations,
  canAct,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  message?: RequestInboxMessage;
  locations: Location[];
  canAct: boolean;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [locationId, setLocationId] = useState("");
  const [lines, setLines] = useState([
    { name: "", specification: "", quantity: "1", unit: "Each", estimatedUnitPrice: "0" },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!message) return;
    setTitle(message.subject);
    setDescription(message.text_body || "");
    setLines([{ name: message.subject, specification: "", quantity: "1", unit: "Each", estimatedUnitPrice: "0" }]);
    setNeededBy("");
    setLocationId(locations[0]?.id || "");
    setReason("");
    setError("");
  }, [message, locations]);
  if (!message) return null;
  async function decide(decision: "ready" | "rejected") {
    setBusy(true);
    setError("");
    try {
      await actions.reviewRequestInboxMessage(
        organisationId,
        message!.id,
        decision,
        reason,
      );
      onDone();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function convert(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await actions.convertRequestInboxMessage(organisationId, message!.id, {
        p_title: title,
        p_description: description,
        p_location_id: locationId,
        p_needed_by: neededBy,
        p_lines: lines.map((line) => ({
          name: line.name,
          specification: line.specification,
          quantity: Number(line.quantity),
          unit: line.unit,
          estimated_unit_price: Number(line.estimatedUnitPrice),
        })),
      });
      onDone();
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function download(path: string) {
    try {
      window.open(
        await requestInboxAttachmentUrl(path),
        "_blank",
        "noopener,noreferrer",
      );
    } catch (failure) {
      setError((failure as Error).message);
    }
  }
  function applySuggestion(value: ExtractionSuggestion) {
    if (value.title) setTitle(value.title);
    if (value.description) setDescription(value.description);
    if (value.neededBy && /^\d{4}-\d{2}-\d{2}$/.test(value.neededBy)) setNeededBy(value.neededBy);
    if (value.lines.length) {
      setLines(value.lines.map((line) => ({
        name: line.name,
        specification: line.specification,
        quantity: String(line.quantity),
        unit: line.unit,
        estimatedUnitPrice: String(line.estimatedUnitPrice),
      })));
    }
  }
  function updateLine(index: number, key: keyof (typeof lines)[number], value: string) {
    setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value } : line));
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader
          eyebrow="IMMUTABLE SOURCE"
          title={message.subject}
          description={`${message.sender_name || message.sender_email} · ${displayDate(message.received_at)}`}
        />
        <div className="space-y-4 p-6">
          <div className="rounded-md border p-3">
            <div className="mb-2 flex items-center justify-between">
              <Badge value={message.status} />
              <span className="font-mono text-xs">
                SHA-256 {message.raw_sha256}
              </span>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm">
              {message.text_body || "No plain-text body supplied."}
            </pre>
          </div>
          {(message.request_inbox_attachments || []).map((item) => (
            <div key={item.id} className="rounded-md border p-3">
              <button
                type="button"
                onClick={() => download(item.object_path)}
                className="flex w-full items-center justify-between text-left"
              >
                <span>
                  <strong>{item.file_name}</strong>
                  <small className="block text-[var(--text-secondary)]">
                    {item.mime_type} · {item.byte_size} bytes ·{" "}
                    {item.sha256.slice(0, 12)}…
                  </small>
                </span>
                <Download className="size-4" />
              </button>
              {canAct && ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(item.mime_type) && (
                <AssistedExtractionCard
                  organisationId={organisationId}
                  sourceId={item.id}
                  onApply={applySuggestion}
                />
              )}
            </div>
          ))}
          {canAct &&
            ["received", "needs_review", "quarantined"].includes(
              message.status,
            ) && (
              <div className="space-y-3 rounded-md border p-3">
                <Label htmlFor="inbox-review-reason">
                  Review note / rejection reason
                </Label>
                <Textarea
                  id="inbox-review-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button disabled={busy} onClick={() => decide("ready")}>
                    <CheckCircle2 className="size-4" />
                    Confirm source
                  </Button>
                  <Button
                    disabled={busy || !reason.trim()}
                    variant="outline"
                    onClick={() => decide("rejected")}
                  >
                    <XCircle className="size-4" />
                    Reject
                  </Button>
                </div>
              </div>
            )}
          {canAct && message.status === "ready" && (
            <form
              className="space-y-3 rounded-md border p-3"
              onSubmit={convert}
            >
              <h3 className="font-semibold">
                Convert to canonical requisition
              </h3>
              <div>
                <Label htmlFor="inbox-title">Title</Label>
                <Input
                  id="inbox-title"
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="inbox-description">Description</Label>
                <Textarea
                  id="inbox-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="inbox-location">Location</Label>
                  <Select
                    id="inbox-location"
                    required
                    value={locationId}
                    onChange={(event) => setLocationId(event.target.value)}
                  >
                    <option value="">Select location</option>
                    {locations.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="inbox-needed">Needed by</Label>
                  <Input
                    id="inbox-needed"
                    type="date"
                    required
                    value={neededBy}
                    onChange={(event) => setNeededBy(event.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Requested lines</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setLines((current) => [...current, { name: "", specification: "", quantity: "1", unit: "Each", estimatedUnitPrice: "0" }])}>Add line</Button>
                </div>
                {lines.map((line, index) => (
                  <div key={index} className="space-y-3 rounded-md border p-3">
                    <div className="flex items-center justify-between"><strong>Line {index + 1}</strong>{lines.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index))}>Remove</Button>}</div>
                    <Input aria-label={`Line ${index + 1} item`} required value={line.name} onChange={(event) => updateLine(index, "name", event.target.value)} placeholder="Item or service" />
                    <Textarea aria-label={`Line ${index + 1} specification`} required value={line.specification} onChange={(event) => updateLine(index, "specification", event.target.value)} placeholder="Specification" />
                    <div className="grid grid-cols-3 gap-3">
                      <Input aria-label={`Line ${index + 1} quantity`} type="number" min="0.0001" step="any" required value={line.quantity} onChange={(event) => updateLine(index, "quantity", event.target.value)} />
                      <Input aria-label={`Line ${index + 1} unit`} required value={line.unit} onChange={(event) => updateLine(index, "unit", event.target.value)} />
                      <Input aria-label={`Line ${index + 1} estimated unit price`} type="number" min="0" step="0.01" required value={line.estimatedUnitPrice} onChange={(event) => updateLine(index, "estimatedUnitPrice", event.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
              <Button type="submit" disabled={busy}>
                {busy ? "Converting…" : "Create evidence-linked draft"}
              </Button>
            </form>
          )}
          {error && <p className="text-sm text-[var(--danger)]">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
